/**
 * CartFlyoutEmptyCart — empty state for the cart flyout drawer.
 * Uses flyout layout + King Living brand typography and button variants.
 */
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import {AccountIcon, BasketIcon} from '@salesforce/retail-react-app/app/components/icons';
import Link from '@salesforce/retail-react-app/app/components/link';
import {
  Box,
  Button,
  Stack,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useCartFlyoutContext} from '@amblique/king-living/app/contexts/cart-flyout-context';

const CartFlyoutEmptyCart = ({isRegistered}) => {
  const {onClose} = useCartFlyoutContext();
  const styles = useMultiStyleConfig('CartFlyout', {});

  return (
    <Box
      className="cart-flyout-empty-cart"
      data-testid="sf-cart-empty"
      {...styles.emptyState}
    >
      <Stack className="cart-flyout-empty-cart__content" {...styles.emptyStateContent}>
        <Box className="cart-flyout-empty-cart__icon" {...styles.emptyStateIcon}>
          <BasketIcon boxSize="100%" />
        </Box>

        <Stack className="cart-flyout-empty-cart__text" {...styles.emptyStateTextBlock}>
          <Text as="p" className="cart-flyout-empty-cart__title" {...styles.emptyStateTitle}>
            <FormattedMessage
              defaultMessage="Your cart is empty."
              id="empty_cart.description.empty_cart"
            />
          </Text>

          <Text as="p" className="cart-flyout-empty-cart__message" {...styles.emptyStateMessage}>
            {isRegistered ? (
              <FormattedMessage
                defaultMessage="Continue shopping to add items to your cart."
                id="empty_cart.message.continue_shopping"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Continue shopping, or sign in for a quicker checkout."
                id="empty_cart.message.sign_in_or_continue_shopping"
              />
            )}
          </Text>
        </Stack>

        <Stack className="cart-flyout-empty-cart__actions" {...styles.emptyStateActions}>
          <Button
            as={Link}
            className="cart-flyout-empty-cart__continue-btn"
            href="/"
            size="md"
            tabIndex={0}
            variant={isRegistered ? 'filled-light' : 'outlined-light'}
            width="100%"
            onClick={onClose}
          >
            <FormattedMessage
              defaultMessage="Continue Shopping"
              id="empty_cart.link.continue_shopping"
            />
          </Button>

          {!isRegistered && (
            <Button
              as={Link}
              className="cart-flyout-empty-cart__sign-in-btn"
              href="/account"
              iconSpacing={2}
              tabIndex={0}
              rightIcon={<AccountIcon aria-hidden boxSize="14px" />}
              size="md"
              variant="filled-light"
              width="100%"
              {...styles.emptyStateSignInBtn}
              onClick={onClose}
            >
              <FormattedMessage defaultMessage="Sign In" id="empty_cart.link.sign_in" />
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

CartFlyoutEmptyCart.displayName = 'CartFlyoutEmptyCart';
CartFlyoutEmptyCart.propTypes = {
  isRegistered: PropTypes.bool,
};

export default CartFlyoutEmptyCart;
